// Inventory data layer (Module 06 §5). TanStack Query hooks wrapping the
// generated api-client — doctrine 3: apps/web talks to the API only through
// `@assessly/api-client`, never a hand-rolled fetch. Query keys live in
// `inventoryKeys`; every mutation sends a fresh `Idempotency-Key` (§5: "all
// mutations require Idempotency-Key") and invalidates the lists it affects.
// Errors are normalised by `unwrap` into `RequestError` (request id + field
// errors) / `UnauthorizedError` (401 → /login).
//
// Redaction note (§7): money fields on movements / counts / adjustment requests
// arrive as `null` with a sibling `redacted: true` marker when the caller may
// not see them, and as `null` with NO marker when the stock is genuinely
// unvalued. Screens must route that through `lib/cost.ts` — never treat a null
// cost as zero.
//
// Degradation note: several §5 reads carry `unavailable` / `owner_module`
// markers for terms an unbound module owns (ATP terms, trace customers,
// off-cut discount hints). Those are rendered as "waiting on Module NN", never
// as 0 / empty — see the individual screens.

import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { newIdempotencyKey, unwrap } from "../../../../lib/request";

type S = components["schemas"];

export type InventoryLocation = S["InventoryLocation"];
export type InventoryLot = S["InventoryLot"];
export type StockUnit = S["StockUnit"];
export type StockMovement = S["StockMovement"];
export type StockLevel = S["StockLevel"];
export type Transfer = S["InventoryTransfer"];
export type TransferLine = S["TransferLine"];
export type CycleCount = S["CycleCount"];
export type CycleCountLine = S["CycleCountLine"];
export type LookupUnitMatch = S["LookupUnitMatch"];
export type LookupOffcutCombo = S["LookupOffcutCombo"];
export type LookupBulkLot = S["LookupBulkLot"];
export type LookupResponse = S["LookupResponse"];
export type TraceResponse = S["TraceResponse"];
export type TraceNode = S["TraceNode"];
export type TraceEdge = S["TraceEdge"];
export type AtpResponse = S["AtpResponse"];
export type AtpTerm = S["AtpTerm"];
export type Facility = S["Facility"];
export type ScanEvent = S["InventoryScanEvent"];

type LocationsQuery = NonNullable<paths["/v1/inventory/locations"]["get"]["parameters"]["query"]>;
type LotsQuery = NonNullable<paths["/v1/inventory/lots"]["get"]["parameters"]["query"]>;
type StockUnitsQuery = NonNullable<
  paths["/v1/inventory/stock-units"]["get"]["parameters"]["query"]
>;
type MovementsQuery = NonNullable<paths["/v1/inventory/movements"]["get"]["parameters"]["query"]>;
type LevelsQuery = NonNullable<paths["/v1/inventory/levels"]["get"]["parameters"]["query"]>;
type TransfersQuery = NonNullable<paths["/v1/inventory/transfers"]["get"]["parameters"]["query"]>;
type CountsQuery = NonNullable<paths["/v1/inventory/cycle-counts"]["get"]["parameters"]["query"]>;
type LookupQuery = NonNullable<paths["/v1/inventory/lookup"]["get"]["parameters"]["query"]>;

export type {
  CountsQuery,
  LevelsQuery,
  LocationsQuery,
  LookupQuery,
  LotsQuery,
  MovementsQuery,
  StockUnitsQuery,
  TransfersQuery,
};

// ---- query keys -------------------------------------------------------------

export const inventoryKeys = {
  all: ["inventory"] as const,
  locations: (query: LocationsQuery = {}) => ["inventory", "locations", query] as const,
  locationsByIds: (ids: readonly string[]) => ["inventory", "locations", "byIds", ids] as const,
  locationContents: (id: string) => ["inventory", "location", id, "contents"] as const,
  lots: (query: LotsQuery = {}) => ["inventory", "lots", query] as const,
  lot: (id: string) => ["inventory", "lot", id] as const,
  trace: (id: string, direction: TraceDirection) =>
    ["inventory", "lot", id, "trace", direction] as const,
  units: (query: StockUnitsQuery = {}) => ["inventory", "stock-units", query] as const,
  unit: (id: string) => ["inventory", "stock-unit", id] as const,
  unitHistory: (id: string, cursor: string | undefined) =>
    ["inventory", "stock-unit", id, "history", cursor ?? null] as const,
  lookup: (query: LookupQuery) => ["inventory", "lookup", query] as const,
  levels: (query: LevelsQuery = {}) => ["inventory", "levels", query] as const,
  atp: (query: { product_id: string; facility_id: string; date?: string }) =>
    ["inventory", "atp", query] as const,
  movements: (query: MovementsQuery = {}) => ["inventory", "movements", query] as const,
  transfers: (query: TransfersQuery = {}) => ["inventory", "transfers", query] as const,
  transfer: (id: string) => ["inventory", "transfer", id] as const,
  counts: (query: CountsQuery = {}) => ["inventory", "cycle-counts", query] as const,
  count: (id: string) => ["inventory", "cycle-count", id] as const,
  facilities: () => ["inventory", "ref", "facilities"] as const,
  uoms: () => ["inventory", "ref", "uoms"] as const,
  product: (id: string) => ["inventory", "ref", "product", id] as const,
  productConversions: (id: string) => ["inventory", "ref", "product", id, "conversions"] as const,
} as const;

const MOVEMENTS = ["inventory", "movements"] as const;
const UNITS = ["inventory", "stock-units"] as const;
const LOCATIONS = ["inventory", "locations"] as const;
const COUNTS = ["inventory", "cycle-counts"] as const;
const LOTS = ["inventory", "lots"] as const;

function idempotentHeader() {
  return { "Idempotency-Key": newIdempotencyKey() } as const;
}

/** Reference data barely changes within a session; one fetch, cached. */
const REF_OPTIONS = { staleTime: 5 * 60_000, gcTime: 30 * 60_000, retry: false } as const;

// =============================================================================
// §4.3 Locations
// =============================================================================

/**
 * ONE page of locations. Correct only when the QUERY narrows the answer to
 * something a page can hold — `id=`, `parent_id=`, `kind=`, or a `q=` search
 * whose results the screen renders as "matches, refine to see fewer".
 *
 * NEVER scan this for a needle: staging's extrusion plant crossed 200 rows on
 * 2026-08-05 and a real factory's bin tree is thousands (the M06-S02 truncation
 * class). The three shapes that are safe live below — `useLocationSearchByKinds` for
 * pickers, `useLocationPathIndex` for id→path labels, `useLocationPages` for the
 * tree browser.
 */
export function useLocations(query: LocationsQuery = {}, enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.locations(query),
    retry: false,
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/locations", { signal, params: { query } }),
        "locations",
      ),
  });
}

/** One picker page. Small on purpose: a picker's job is to make the needle
 * REACHABLE by search, not to render a warehouse. */
export const LOCATION_SEARCH_LIMIT = 20;

/**
 * The SERVER-side location search behind every picker (§5 row 1's `q`, which
 * matches code, name and materialized path).
 *
 * The house precedent is module 01's exact-SKU pull-up: the thing you are
 * looking for must be reachable regardless of which page it would otherwise
 * fall on. A picker that filtered a fetched page client-side could not offer a
 * bin it never fetched, and a storekeeper cannot move a pallet to a bin the
 * destination list refuses to show.
 *
 * Each kind the caller names is narrowed on the SERVER (task #253,
 * D-330). §2.1 lets a bin hang under a zone OR a warehouse, and §5 row 1 takes
 * one `kind`, so a picker that must offer both runs one search per kind rather
 * than one unfiltered search narrowed in the browser, which would re-open the
 * truncation class on a write path: a facility's first page is mostly bins.
 *
 * EACH KIND HAS ITS OWN PAGE. Before anything is typed the picker browses, and
 * a shared page over two kinds is the first twenty rows of the facility in path
 * order, which on a large plant is warehouses and nothing else. With a page per
 * kind the bin's parent picker opens on twenty zones and twenty warehouses.
 * The merged rows come back in path order, and `more` is true when any kind's
 * page was full or carried a cursor. An empty `kinds` searches every kind once.
 */
export function useLocationSearchByKinds(input: {
  q: string;
  facilityId?: string | undefined;
  kinds: readonly string[];
  isActive?: boolean | undefined;
  isCountable?: boolean | undefined;
}) {
  const base: LocationsQuery = { limit: LOCATION_SEARCH_LIMIT };
  if (input.facilityId !== undefined && input.facilityId.length > 0) {
    base.facility_id = input.facilityId;
  }
  if (input.q.trim().length > 0) base.q = input.q.trim();
  if (input.isActive !== undefined) base.is_active = input.isActive;
  if (input.isCountable !== undefined) base.is_countable = input.isCountable;
  const queries: LocationsQuery[] =
    input.kinds.length === 0 ? [base] : input.kinds.map((kind) => ({ ...base, kind }));
  return useQueries({
    queries: queries.map((query) => ({
      queryKey: inventoryKeys.locations(query),
      retry: false,
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        unwrap(
          await client.GET("/v1/inventory/locations", { signal, params: { query } }),
          "locations",
        ),
    })),
    combine: (results) => {
      const seen = new Set<string>();
      const items: InventoryLocation[] = [];
      for (const result of results) {
        for (const location of result.data?.items ?? []) {
          if (seen.has(location.id)) continue;
          seen.add(location.id);
          items.push(location);
        }
      }
      items.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
      return {
        items,
        more: results.some(
          (r) =>
            (r.data?.next_cursor ?? null) !== null ||
            (r.data?.items.length ?? 0) >= LOCATION_SEARCH_LIMIT,
        ),
        isPending: results.some((r) => r.isPending),
        error: results.find((r) => r.error !== null)?.error ?? null,
      };
    },
  });
}

/** The server caps a page at 200, and `id=` is itself capped at 200 ids. */
const LOCATION_ID_CHUNK = 200;

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * Resolve a KNOWN, BOUNDED set of location ids to their paths — the id→path
 * label map behind a ledger page, a count's variance rows, a floor worksheet.
 *
 * The same shape as `useProductIndex`, and for the same reason: label the ids
 * on THIS page, never walk the tree. The three screens that used to build this
 * map from one 200-row page rendered a blank (or a raw uuid) wherever the bin
 * sat past row 200 — and the fix is not to walk 500 rows to label 12, it is to
 * ask for the 12. Chunked at the endpoint's own cap so an unusually wide page
 * still resolves completely; the server sizes the limit from the id count, so a
 * chunk can never come back short.
 */
export function useLocationPathIndex(ids: readonly string[]) {
  const unique = Array.from(new Set(ids.filter((id) => id.length > 0))).sort();
  const groups = chunk(unique, LOCATION_ID_CHUNK);
  return useQueries({
    queries: groups.map((group) => ({
      ...REF_OPTIONS,
      queryKey: inventoryKeys.locationsByIds(group),
      queryFn: async ({ signal }) =>
        unwrap(
          await client.GET("/v1/inventory/locations", {
            signal,
            params: { query: { id: group, limit: LOCATION_ID_CHUNK } },
          }),
          "locations-by-id",
        ),
    })),
    combine: (results) => {
      const byId = new Map<string, InventoryLocation>();
      for (const result of results) {
        for (const location of result.data?.items ?? []) byId.set(location.id, location);
      }
      return {
        byId,
        pathOf: (id: string) => byId.get(id)?.path,
        isPending: results.some((r) => r.isPending),
      };
    },
  });
}

/**
 * The §4.3 tree browser's read: cursor pages, accumulated on demand.
 *
 * Explicitly NOT auto-draining every page — a tenant's whole tree can be tens
 * of thousands of bins and a browser that silently fetched all of them would
 * trade one failure (a wrong tree) for another (a dead tab). The screen states
 * the row count and offers "load more", so what is on screen is always a fact
 * the user was told.
 */
export function useLocationPages(query: LocationsQuery = {}) {
  return useInfiniteQuery({
    queryKey: [...inventoryKeys.locations(query), "pages"] as const,
    retry: false,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) =>
      unwrap(
        await client.GET("/v1/inventory/locations", {
          signal,
          params: {
            query: { ...query, ...(pageParam !== undefined ? { cursor: pageParam } : {}) },
          },
        }),
        "locations",
      ),
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });
}

export function useLocationContents(id: string | undefined) {
  return useQuery({
    queryKey: inventoryKeys.locationContents(id ?? ""),
    retry: false,
    enabled: id !== undefined && id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/locations/{id}/contents", {
          signal,
          params: { path: { id: id ?? "" } },
        }),
        "location-contents",
      ),
  });
}

/** §5 row 2 — create a warehouse / zone / bin (the §4.3 empty-state CTA). */
export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["LocationCreateRequest"]) =>
      unwrap(
        await client.POST("/v1/inventory/locations", {
          params: { header: idempotentHeader() },
          body,
        }),
        "create-location",
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LOCATIONS });
    },
  });
}

/**
 * §5 row 5 — batch QR label PDF for a set of locations. Renders through Module
 * 02's print-kit surface, so the resulting batch also satisfies the go-live
 * checklist's channels.qr_kit_printed. Not cached and not invalidating: a print
 * changes nothing about the tree.
 */
export function usePrintLocationQrLabels() {
  return useMutation({
    mutationFn: async (body: S["LocationQrLabelsRequest"]) =>
      unwrap(
        await client.POST("/v1/inventory/locations/qr-labels", {
          params: { header: idempotentHeader() },
          body,
        }),
        "print-location-qr-labels",
      ),
  });
}

/** §5 row 3 — rename / re-parent / toggle. The drag-to-reparent write path. */
export function usePatchLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: S["LocationPatchRequest"] }) =>
      unwrap(
        await client.PATCH("/v1/inventory/locations/{id}", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: input.body,
        }),
        "patch-location",
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LOCATIONS });
    },
  });
}

// =============================================================================
// §4.6 Lots · §4.7 Trace
// =============================================================================

export function useLots(query: LotsQuery = {}, enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.lots(query),
    retry: false,
    // Callers that only want lots FOR a product must be able to say so: an
    // unguarded `useLots({})` fires an unfiltered tenant-wide lots read whose
    // rows the caller then never uses (transfers-list.tsx did exactly that for
    // every line with no product chosen yet).
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/inventory/lots", { signal, params: { query } }), "lots"),
  });
}

export function useLot(id: string) {
  return useQuery({
    queryKey: inventoryKeys.lot(id),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/lots/{id}", { signal, params: { path: { id } } }),
        "lot-detail",
      ),
  });
}

export type TraceDirection = "up" | "down";

export function useLotTrace(id: string, direction: TraceDirection, enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.trace(id, direction),
    retry: false,
    enabled: enabled && id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/lots/{id}/trace", {
          signal,
          params: { path: { id }, query: { direction } },
        }),
        "lot-trace",
      ),
  });
}

export function useQuarantineLot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; reason: string }) =>
      unwrap(
        await client.POST("/v1/inventory/lots/{id}/quarantine", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: { reason: input.reason },
        }),
        "quarantine-lot",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: inventoryKeys.lot(input.id) });
      void qc.invalidateQueries({ queryKey: LOTS });
      void qc.invalidateQueries({ queryKey: UNITS });
    },
  });
}

export function useReleaseLot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; note?: string }) =>
      unwrap(
        await client.POST("/v1/inventory/lots/{id}/release", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: input.note !== undefined ? { note: input.note } : {},
        }),
        "release-lot",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: inventoryKeys.lot(input.id) });
      void qc.invalidateQueries({ queryKey: LOTS });
      void qc.invalidateQueries({ queryKey: UNITS });
    },
  });
}

// =============================================================================
// §4.1 Lookup · §4.2 Stock units
// =============================================================================

export function useLookup(query: LookupQuery, enabled: boolean) {
  return useQuery({
    queryKey: inventoryKeys.lookup(query),
    retry: false,
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/inventory/lookup", { signal, params: { query } }), "lookup"),
  });
}

export function useStockUnits(query: StockUnitsQuery = {}, enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.units(query),
    retry: false,
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/stock-units", { signal, params: { query } }),
        "stock-units",
      ),
  });
}

export function useStockUnit(id: string) {
  return useQuery({
    queryKey: inventoryKeys.unit(id),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/stock-units/{id}", { signal, params: { path: { id } } }),
        "stock-unit",
      ),
  });
}

export function useStockUnitHistory(id: string, cursor: string | undefined, limit = 50) {
  return useQuery({
    queryKey: inventoryKeys.unitHistory(id, cursor),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/stock-units/{id}/history", {
          signal,
          params: { path: { id }, query: cursor !== undefined ? { cursor, limit } : { limit } },
        }),
        "stock-unit-history",
      ),
  });
}

export function useQuarantineUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; reason: string; relocate: boolean }) =>
      unwrap(
        await client.POST("/v1/inventory/stock-units/{id}/quarantine", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: { reason: input.reason, relocate: input.relocate },
        }),
        "quarantine-unit",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: inventoryKeys.unit(input.id) });
      void qc.invalidateQueries({ queryKey: UNITS });
      void qc.invalidateQueries({ queryKey: MOVEMENTS });
    },
  });
}

export function useReleaseUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; note?: string; to_location_id?: string }) => {
      const body: S["UnitReleaseRequest"] = {};
      if (input.note !== undefined) body.note = input.note;
      if (input.to_location_id !== undefined) body.to_location_id = input.to_location_id;
      return unwrap(
        await client.POST("/v1/inventory/stock-units/{id}/release-unit", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body,
        }),
        "release-unit",
      );
    },
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: inventoryKeys.unit(input.id) });
      void qc.invalidateQueries({ queryKey: UNITS });
      void qc.invalidateQueries({ queryKey: MOVEMENTS });
    },
  });
}

/** §5 row 16 — the §3.2 cut. `reason: 'qc_fail'` quarantines the children. */
export function useCutUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: S["CutRequest"] }) =>
      unwrap(
        await client.POST("/v1/inventory/stock-units/{id}/cut", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: input.body,
        }),
        "cut-unit",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: inventoryKeys.unit(input.id) });
      void qc.invalidateQueries({ queryKey: UNITS });
      void qc.invalidateQueries({ queryKey: MOVEMENTS });
      void qc.invalidateQueries({ queryKey: ["inventory", "lookup"] });
    },
  });
}

/** §5 row 17 — correct a measure; the service posts the adjustment movement. */
export function useRemeasureUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; entered_measure: string }) =>
      unwrap(
        await client.POST("/v1/inventory/stock-units/{id}/remeasure", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: { entered_measure: input.entered_measure },
        }),
        "remeasure-unit",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: inventoryKeys.unit(input.id) });
      void qc.invalidateQueries({ queryKey: UNITS });
      void qc.invalidateQueries({ queryKey: MOVEMENTS });
    },
  });
}

/** §5 row 19 — retire a drained/bare container (maps to `inventory.adjust`). */
export function useRetireUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; reason_code: S["UnitRetireRequest"]["reason_code"] }) =>
      unwrap(
        await client.POST("/v1/inventory/stock-units/{id}/retire", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: { reason_code: input.reason_code },
        }),
        "retire-unit",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: inventoryKeys.unit(input.id) });
      void qc.invalidateQueries({ queryKey: UNITS });
      void qc.invalidateQueries({ queryKey: MOVEMENTS });
    },
  });
}

// =============================================================================
// Reservations (§4.1 Reserve, §4.2 Reservations tab)
// =============================================================================

type ReservationsQuery = NonNullable<
  paths["/v1/inventory/reservations"]["get"]["parameters"]["query"]
>;

export function useReservations(query: ReservationsQuery, enabled: boolean) {
  return useQuery({
    queryKey: ["inventory", "reservations", query] as const,
    retry: false,
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/reservations", { signal, params: { query } }),
        "reservations",
      ),
  });
}

export function useCreateReservation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["ReservationCreateRequest"]) =>
      unwrap(
        await client.POST("/v1/inventory/reservations", {
          params: { header: idempotentHeader() },
          body,
        }),
        "create-reservation",
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inventory", "reservations"] });
      void qc.invalidateQueries({ queryKey: ["inventory", "lookup"] });
      void qc.invalidateQueries({ queryKey: UNITS });
      void qc.invalidateQueries({ queryKey: ["inventory", "atp"] });
    },
  });
}

/** §5 row 30 — release a hold. The §4.1/§4.2 "Release" control. */
export function useReleaseReservation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) =>
      unwrap(
        await client.POST("/v1/inventory/reservations/{id}/release", {
          params: { path: { id: input.id }, header: idempotentHeader() },
        }),
        "release-reservation",
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inventory", "reservations"] });
      void qc.invalidateQueries({ queryKey: UNITS });
      void qc.invalidateQueries({ queryKey: ["inventory", "atp"] });
    },
  });
}

// =============================================================================
// §4.8 Movements ledger
// =============================================================================

/**
 * §5 row 25 — manual adjustment / repack / scrap / issue. The response is
 * EITHER a posted movement (201) or the §3.9 approval hold (202 with
 * `adjustment_request_id`); the caller must render both, never assume a post.
 */
export function useCreateMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["MovementCreateRequest"]) => {
      const result = await client.POST("/v1/inventory/movements", {
        params: { header: idempotentHeader() },
        body,
      });
      const data = unwrap(result, "create-movement");
      return { data, status: result.response.status };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MOVEMENTS });
      void qc.invalidateQueries({ queryKey: UNITS });
      void qc.invalidateQueries({ queryKey: ["inventory", "levels"] });
    },
  });
}

export function useMovements(query: MovementsQuery = {}) {
  return useQuery({
    queryKey: inventoryKeys.movements(query),
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/movements", { signal, params: { query } }),
        "movements",
      ),
  });
}

/** §5 row 26 — the row-expand "Reverse" action, permission-gated in the UI. */
export function useReverseMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; note?: string }) =>
      unwrap(
        await client.POST("/v1/inventory/movements/{id}/reverse", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: input.note !== undefined ? { note: input.note } : {},
        }),
        "reverse-movement",
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MOVEMENTS });
      void qc.invalidateQueries({ queryKey: UNITS });
    },
  });
}

// =============================================================================
// §4.9 ATP · levels
// =============================================================================

export function useAtp(
  query: { product_id: string; facility_id: string; date?: string },
  enabled: boolean,
) {
  return useQuery({
    queryKey: inventoryKeys.atp(query),
    retry: false,
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/inventory/atp", { signal, params: { query } }), "atp"),
  });
}

export function useLevels(query: LevelsQuery = {}, enabled = true) {
  return useQuery({
    queryKey: inventoryKeys.levels(query),
    retry: false,
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/inventory/levels", { signal, params: { query } }), "levels"),
  });
}

// =============================================================================
// §4.4 Transfers (office half)
// =============================================================================

/** §5 row 30b — create an inter-facility transfer (§4.4 "New transfer"). */
export function useCreateTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["TransferCreateRequest"]) =>
      unwrap(
        await client.POST("/v1/inventory/transfers", {
          params: { header: idempotentHeader() },
          body,
        }),
        "create-transfer",
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inventory", "transfers"] });
    },
  });
}

/** §5 row 33 — edit a DRAFT transfer (header fields + lines). */
export function usePatchTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: S["TransferPatchRequest"] }) =>
      unwrap(
        await client.PATCH("/v1/inventory/transfers/{id}", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: input.body,
        }),
        "patch-transfer",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: inventoryKeys.transfer(input.id) });
      void qc.invalidateQueries({ queryKey: ["inventory", "transfers"] });
    },
  });
}

export function useTransfers(query: TransfersQuery = {}) {
  return useQuery({
    queryKey: inventoryKeys.transfers(query),
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/transfers", { signal, params: { query } }),
        "transfers",
      ),
  });
}

export function useTransfer(id: string) {
  return useQuery({
    queryKey: inventoryKeys.transfer(id),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/transfers/{id}", { signal, params: { path: { id } } }),
        "transfer",
      ),
  });
}

export function useDispatchTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) =>
      unwrap(
        await client.POST("/v1/inventory/transfers/{id}/dispatch", {
          params: { path: { id: input.id }, header: idempotentHeader() },
        }),
        "dispatch-transfer",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: inventoryKeys.transfer(input.id) });
      void qc.invalidateQueries({ queryKey: ["inventory", "transfers"] });
      void qc.invalidateQueries({ queryKey: MOVEMENTS });
    },
  });
}

export function useCancelTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; reason: string }) =>
      unwrap(
        await client.POST("/v1/inventory/transfers/{id}/cancel", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: { reason: input.reason },
        }),
        "cancel-transfer",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: inventoryKeys.transfer(input.id) });
      void qc.invalidateQueries({ queryKey: ["inventory", "transfers"] });
    },
  });
}

// =============================================================================
// §4.5 Cycle count review (office half)
// =============================================================================

export function useCycleCounts(query: CountsQuery = {}) {
  return useQuery({
    queryKey: inventoryKeys.counts(query),
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/cycle-counts", { signal, params: { query } }),
        "cycle-counts",
      ),
  });
}

export function useCycleCount(id: string) {
  return useQuery({
    queryKey: inventoryKeys.count(id),
    retry: false,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/cycle-counts/{id}", { signal, params: { path: { id } } }),
        "cycle-count",
      ),
  });
}

/**
 * §5 row 35 — open a count over a countable location (`inventory.count.perform`).
 * A count that can be STARTED is the other half of a review inbox: without it
 * the office can only read counts a seed or an agent created.
 */
export function useCreateCycleCount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S["CycleCountCreateRequest"]) =>
      unwrap(
        await client.POST("/v1/inventory/cycle-counts", {
          params: { header: idempotentHeader() },
          body,
        }),
        "create-cycle-count",
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: COUNTS });
    },
  });
}

/** §5 row 43 — abandon a count (`inventory.count.review`). */
export function useCancelCycleCount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; reason: string }) =>
      unwrap(
        await client.POST("/v1/inventory/cycle-counts/{id}/cancel", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: { reason: input.reason },
        }),
        "cancel-cycle-count",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: inventoryKeys.count(input.id) });
      void qc.invalidateQueries({ queryKey: COUNTS });
    },
  });
}

export function useReviewCountLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      countId: string;
      lineId: string;
      action: "accept" | "reject" | "recount";
      note?: string;
    }) => {
      const body: S["CountLineReviewRequest"] = { action: input.action };
      if (input.note !== undefined) body.note = input.note;
      return unwrap(
        await client.POST("/v1/inventory/cycle-counts/{id}/lines/{lineId}/review", {
          params: { path: { id: input.countId, lineId: input.lineId }, header: idempotentHeader() },
          body,
        }),
        "review-count-line",
      );
    },
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: inventoryKeys.count(input.countId) });
      void qc.invalidateQueries({ queryKey: COUNTS });
    },
  });
}

/**
 * §5 row 44 / §3.4.6 — approve + post under the D-049 LOCAL tier while Module
 * 12's chain is unbound. The response decides what the screen says inline:
 * `posted` (movements posted), `escalated` (above the caller's tier →
 * pending_approval), or `moved_line_ids` (§9.11 fresh-compare flipped lines
 * back to recount and nothing posted).
 */
export function useApproveCount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) =>
      unwrap(
        await client.POST("/v1/inventory/cycle-counts/{id}/approve", {
          params: { path: { id: input.id }, header: idempotentHeader() },
        }),
        "approve-count",
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: inventoryKeys.count(input.id) });
      void qc.invalidateQueries({ queryKey: COUNTS });
      void qc.invalidateQueries({ queryKey: MOVEMENTS });
      void qc.invalidateQueries({ queryKey: ["inventory", "levels"] });
    },
  });
}

// =============================================================================
// §4.11 Adjustment requests — the §3.9 approval hold
// =============================================================================
//
// POST /movements answers 202 with an `adjustment_request_id` whenever the
// value gate holds an adjustment (above threshold, or unvalued ⇒ treated as
// large). Until this surface existed the hold vanished: the requester saw one
// line of text and the approver had no inbox at all. These four calls are the
// D-049 LOCAL tier — decide is `inventory.count.review`, cancel is
// `inventory.adjust` (the same chain slot Module 12 takes over when it binds).

type AdjustmentRequestsQuery = NonNullable<
  paths["/v1/inventory/adjustment-requests"]["get"]["parameters"]["query"]
>;

export type AdjustmentRequest = S["AdjustmentRequest"];
type AdjustmentRequestDetail = S["AdjustmentRequestDetailResponse"];
export type { AdjustmentRequestsQuery };

const ADJUSTMENT_REQUESTS = ["inventory", "adjustment-requests"] as const;

export function useAdjustmentRequests(query: AdjustmentRequestsQuery = {}) {
  return useQuery({
    queryKey: ["inventory", "adjustment-requests", query] as const,
    retry: false,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/adjustment-requests", { signal, params: { query } }),
        "adjustment-requests",
      ),
  });
}

export function useAdjustmentRequest(
  id: string,
  enabled = true,
  options: {
    /** Re-read on this interval while it returns a number (§4.12's request
     * page asks every 30 s while the request is pending and the tab visible). */
    refetchInterval?: (status: string | undefined) => number | false;
  } = {},
) {
  const interval = options.refetchInterval;
  return useQuery({
    queryKey: ["inventory", "adjustment-request", id] as const,
    retry: false,
    enabled: enabled && id.length > 0,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/adjustment-requests/{id}", {
          signal,
          params: { path: { id } },
        }),
        "adjustment-request",
      ),
    refetchInterval: (query: { state: { data: AdjustmentRequestDetail | undefined } }) =>
      interval === undefined ? false : interval(query.state.data?.request.status),
  });
}

/** Approve re-validates against FRESH stock and posts; reject requires a note. */
export function useDecideAdjustmentRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; action: "approve" | "reject"; note?: string }) => {
      const body: S["AdjustmentRequestDecideRequest"] = { action: input.action };
      if (input.note !== undefined) body.note = input.note;
      return unwrap(
        await client.POST("/v1/inventory/adjustment-requests/{id}/decide", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body,
        }),
        "decide-adjustment-request",
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADJUSTMENT_REQUESTS });
      void qc.invalidateQueries({ queryKey: ["inventory", "adjustment-request"] });
      void qc.invalidateQueries({ queryKey: MOVEMENTS });
      void qc.invalidateQueries({ queryKey: UNITS });
      void qc.invalidateQueries({ queryKey: ["inventory", "levels"] });
      void qc.invalidateQueries({ queryKey: LOTS });
      void qc.invalidateQueries({ queryKey: ["inventory", "lot"] });
      void qc.invalidateQueries({ queryKey: ["inventory", "lookup"] });
      void qc.invalidateQueries({ queryKey: ["inventory", "location"] });
    },
  });
}

export function useCancelAdjustmentRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; note?: string }) =>
      unwrap(
        await client.POST("/v1/inventory/adjustment-requests/{id}/cancel", {
          params: { path: { id: input.id }, header: idempotentHeader() },
          body: input.note !== undefined ? { note: input.note } : {},
        }),
        "cancel-adjustment-request",
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADJUSTMENT_REQUESTS });
      void qc.invalidateQueries({ queryKey: ["inventory", "adjustment-request"] });
    },
  });
}

// =============================================================================
// §4.12 Stock Adjustment (D-315) — the office door onto §5 rows 25 and 48
// =============================================================================

/** What the §3.9 gate did with an adjustment: posted it, or held it for a
 * decision. Both are success, and the screen must say which (a 202 means
 * NOTHING changed on the books yet). */
export type AdjustOutcome =
  | { readonly kind: "posted"; readonly movementId: string }
  | { readonly kind: "held"; readonly requestId: string };

export interface AdjustStockInput {
  readonly body: S["AdjustmentRequestCreateRequest"];
  /** Route to approval whatever the value (§5 row 48, POST /adjustment-requests). */
  readonly alwaysHold: boolean;
  /** One key per INTENT (`useIntentKey`): a retry after a lost response
   * replays rather than posting twice. */
  readonly idempotencyKey: string;
}

/**
 * §4.12's submit. POST /movements type `adjustment` is the gate's own door:
 * 201 posts, 202 holds. The explicit hold is POST /adjustment-requests, which
 * always answers 202. The KEY is the caller's, not minted here — every other
 * mutation in this file mints per call, which is right for a one-shot button
 * and wrong for a form a person may press twice after a dropped response.
 *
 * It invalidates everything an adjustment moves: the ledger, the level cache,
 * units (a unit's lot page reads its stock summary), lots and a lot's detail,
 * the lookup, every location's contents and the request lists.
 */
export function useAdjustStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AdjustStockInput): Promise<AdjustOutcome> => {
      const header = { "Idempotency-Key": input.idempotencyKey } as const;
      if (input.alwaysHold) {
        const held = unwrap(
          await client.POST("/v1/inventory/adjustment-requests", {
            params: { header },
            body: input.body,
          }),
          "create-adjustment-request",
        );
        return { kind: "held", requestId: held.adjustment_request_id };
      }
      const data = unwrap(
        await client.POST("/v1/inventory/movements", {
          params: { header },
          body: input.body,
        }),
        "adjust-stock",
      );
      if ("adjustment_request_id" in data) {
        return { kind: "held", requestId: data.adjustment_request_id };
      }
      return { kind: "posted", movementId: data.movement_id };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MOVEMENTS });
      void qc.invalidateQueries({ queryKey: UNITS });
      void qc.invalidateQueries({ queryKey: ["inventory", "levels"] });
      void qc.invalidateQueries({ queryKey: LOTS });
      void qc.invalidateQueries({ queryKey: ["inventory", "lot"] });
      void qc.invalidateQueries({ queryKey: ["inventory", "lookup"] });
      void qc.invalidateQueries({ queryKey: ["inventory", "location"] });
      void qc.invalidateQueries({ queryKey: ADJUSTMENT_REQUESTS });
      void qc.invalidateQueries({ queryKey: ["inventory", "adjustment-request"] });
    },
  });
}

/**
 * The work order a spec 04 S2/S7 link came from, for the form's context line
 * (its number, and whether it is closed). Module 04 owns it; this is a read
 * through its own API. A reader it refuses sees the short id instead.
 */
export function useWorkOrderRef(id: string) {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: ["inventory", "ref", "work-order", id] as const,
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/work-orders/{id}", { signal, params: { path: { id } } }),
        "work-order",
      ),
    select: (detail) => ({
      woNo: detail.work_order.wo_no,
      status: detail.work_order.status,
      facilityId: detail.work_order.facility_id,
    }),
  });
}

/** One product's catalogue card (tracking + base UoM), cached like the index. */
export function useProductCard(id: string) {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: inventoryKeys.product(id),
    enabled: id.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/catalogue/products/{id}", { signal, params: { path: { id } } }),
        "product",
      ),
  });
}

/**
 * The ONE location of a kind a plant holds — its `supplier`, `production` or
 * `scrap` counter location (provisioned with the facility, one each). A
 * missing one is an answer the form states, never a location it guesses.
 */
export function usePlantLocationOfKind(facilityId: string, kind: string) {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: inventoryKeys.locations({ facility_id: facilityId, kind, limit: 1 }),
    enabled: facilityId.length > 0 && kind.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/inventory/locations", {
          signal,
          params: { query: { facility_id: facilityId, kind, limit: 1 } },
        }),
        "locations",
      ),
    select: (page) => page.items[0],
  });
}

// =============================================================================
// Reference data (facilities / UoMs / products) — cross-module READS through
// the same generated client. Product names live in Module 01; the inventory
// wire carries ids, so every list resolves the handful of ids on its page.
// =============================================================================

export function useFacilities() {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: inventoryKeys.facilities(),
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/org/facilities", { signal, params: { query: { status: "active" } } }),
        "facilities",
      ),
  });
}

export function useUomIndex() {
  return useQuery({
    ...REF_OPTIONS,
    queryKey: inventoryKeys.uoms(),
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/catalogue/uoms", { signal }), "uoms"),
  });
}

export interface ProductRef {
  readonly id: string;
  readonly sku: string;
  readonly name: string;
  readonly base_uom_id: string;
}

/**
 * Resolve a SET of product ids to `{sku, name}` for a page of inventory rows.
 * One cached query per product (staleTime 5 min), so a 50-row ledger page with
 * eight distinct products costs eight requests once and none thereafter. There
 * is no batch product read in Module 01's §5 — inventing a client-side "fetch
 * everything" would be worse (an unbounded list read on every screen).
 */
export function useProductIndex(ids: readonly string[]) {
  const unique = Array.from(new Set(ids.filter((id) => id.length > 0))).sort();
  return useQueries({
    queries: unique.map((id) => ({
      ...REF_OPTIONS,
      queryKey: inventoryKeys.product(id),
      queryFn: async ({ signal }) =>
        unwrap(
          await client.GET("/v1/catalogue/products/{id}", { signal, params: { path: { id } } }),
          "product",
        ),
    })),
    combine: (results) => {
      const byId = new Map<string, ProductRef>();
      for (const result of results) {
        const product = result.data;
        if (product !== undefined) {
          byId.set(product.id, {
            id: product.id,
            sku: product.sku,
            name: product.name,
            base_uom_id: product.base_uom_id,
          });
        }
      }
      return { byId, isPending: results.some((r) => r.isPending) };
    },
  });
}

/**
 * Resolve a SET of stock-unit ids to their cards (serial + status), the same
 * per-id cached pattern as `useProductIndex`: a transfer's lines name units by
 * id, and §5 row 12's list filters cannot take an id set.
 */
export function useStockUnitIndex(ids: readonly string[]) {
  const unique = Array.from(new Set(ids.filter((id) => id.length > 0))).sort();
  return useQueries({
    queries: unique.map((id) => ({
      ...REF_OPTIONS,
      queryKey: inventoryKeys.unit(id),
      queryFn: async ({ signal }) =>
        unwrap(
          await client.GET("/v1/inventory/stock-units/{id}", { signal, params: { path: { id } } }),
          "stock-unit",
        ),
    })),
    combine: (results) => {
      const byId = new Map<string, StockUnit>();
      for (const result of results) {
        if (result.data !== undefined) byId.set(result.data.unit.id, result.data.unit);
      }
      return { byId, isPending: results.some((r) => r.isPending) };
    },
  });
}

/**
 * Resolve a SET of lot ids to their codes — the §4.3 contents table and the
 * §4.8 row-expand name lots, and a uuid fragment is not a lot code.
 */
export function useLotIndex(ids: readonly string[]) {
  const unique = Array.from(new Set(ids.filter((id) => id.length > 0))).sort();
  return useQueries({
    queries: unique.map((id) => ({
      ...REF_OPTIONS,
      queryKey: inventoryKeys.lot(id),
      queryFn: async ({ signal }) =>
        unwrap(
          await client.GET("/v1/inventory/lots/{id}", { signal, params: { path: { id } } }),
          "lot-detail",
        ),
    })),
    combine: (results) => {
      const byId = new Map<string, InventoryLot>();
      for (const result of results) {
        if (result.data !== undefined) byId.set(result.data.lot.id, result.data.lot);
      }
      return { byId, isPending: results.some((r) => r.isPending) };
    },
  });
}

/**
 * Principal id → display name for the §4.4 timeline, the §4.8 performer column
 * and the §4.5 counter attribution ("performer incl. agent principals", §4.8).
 *
 * ONE list read, not one per id: `GET /v1/members` is gated on
 * `identity.view_members`, which only group_admin and facility_admin hold, so
 * the fetch is issued only when the caller's roles carry it (`enabled`) — a
 * storekeeper would otherwise fire a guaranteed 403 on every ledger page. When
 * it is not fetched, or a principal is not a member (an AGENT principal is
 * not), the screens fall back to the short id in a `<code>` chip through
 * `RefLabel`, which reads as an identifier rather than as a broken name.
 */
export function usePrincipalIndex(enabled: boolean) {
  const query = useQuery({
    ...REF_OPTIONS,
    queryKey: ["inventory", "ref", "members"] as const,
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/members", { signal, params: { query: { limit: 200 } } }),
        "members",
      ),
  });
  const byId = new Map<string, string>();
  for (const member of query.data?.items ?? []) byId.set(member.user_id, member.display_name);
  return { byId, isPending: enabled && query.isPending, enabled };
}

/** §4.10 — Module 01 owns conversions; this read powers the warning list. */
export function useProductConversions(ids: readonly string[]) {
  const unique = Array.from(new Set(ids.filter((id) => id.length > 0))).sort();
  return useQueries({
    queries: unique.map((id) => ({
      ...REF_OPTIONS,
      queryKey: inventoryKeys.productConversions(id),
      queryFn: async ({ signal }) =>
        unwrap(
          await client.GET("/v1/catalogue/products/{id}/conversions", {
            signal,
            params: { path: { id } },
          }),
          "product-conversions",
        ),
    })),
    combine: (results) => {
      const byId = new Map<string, readonly S["ProductConversion"][]>();
      results.forEach((result, index) => {
        const id = unique[index];
        if (id !== undefined && result.data !== undefined) byId.set(id, result.data.items);
      });
      return { byId, isPending: results.some((r) => r.isPending) };
    },
  });
}
